<script setup lang="ts">
import { Filters } from "~/core/models/filters";
import { roundToTwoDecimals } from "~/core/utils/number";
import { useFetchDtwinMetricsCertificate } from "~/modules/dashboard/composables/useFetchDtwinMetricsCertificate";
import type { DtwinsMetricsCertificateData } from "~/modules/dashboard/models/DtwinsMetricsCertificateData.interface";
import { DtwinsMetricsCertificateKey } from "~/modules/dashboard/models/DtwinsMetricsCertificateKey.enum";
import type { DtwinsMetricsCertificateMap } from "~/modules/dashboard/models/DtwinsMetricsCertificateMap.interface";
import { DtwinCertificateImg } from "~/modules/dtwin/models/DtwinCertificateImg.enum";

const props = defineProps<{
  organizationCode?: number;
  selectedSectorCodes?: number[];
  userAssignedSectorsCodes: number[];
}>();

const { isLoading, error, result, fetchData, setFilter } =
  useFetchDtwinMetricsCertificate({
    organizationCode: props.organizationCode,
  });
const { t } = useI18n();

const selectedSectorsComputed = computed(() => {
  if (props.selectedSectorCodes && props.selectedSectorCodes.length > 0) {
    return props.selectedSectorCodes.map((sectorCode) => sectorCode.toString());
  } else {
    return props.userAssignedSectorsCodes;
  }
});

const certificateMap: DtwinsMetricsCertificateMap[] = [
  {
    key: DtwinsMetricsCertificateKey.EXPIRED,
    icon: DtwinCertificateImg.EXPIRED,
    color: "#B54708",
  },
  {
    key: DtwinsMetricsCertificateKey.LESS_THAN_ONE_MONTH,
    icon: DtwinCertificateImg.VALIDITY_LESS_THAN_ONE_MONTH,
    color: "#B54708",
  },
  {
    key: DtwinsMetricsCertificateKey.LESS_THAN_THREE_MONTHS,
    icon: DtwinCertificateImg.VALIDITY_LESS_THAN_THREE_MONTHS,
    color: "#FDB022",
  },
  {
    key: DtwinsMetricsCertificateKey.LESS_THAN_SIX_MONTHS,
    icon: DtwinCertificateImg.VALIDITY_LESS_THAN_SIX_MONTHS,
    color: "#32D583",
  },
  {
    key: DtwinsMetricsCertificateKey.MORE_THAN_SIX_MONTHS,
    icon: DtwinCertificateImg.VALIDITY_GREATER_THAN_SIX_MONTHS,
    color: "#32D583",
  },
];

const certificateData: ComputedRef<DtwinsMetricsCertificateData[]> = computed(
  () => {
    return certificateMap.map((certificate) => {
      const count: number = result.value?.[certificate.key] ?? 0;
      return {
        ...certificate,
        count,
        percentage: roundToTwoDecimals(
          totalCount.value > 0 ? (count * 100) / totalCount.value : 0,
        ),
        label: t(`dashboard.certificates.${certificate.key}`),
      };
    });
  },
);

const totalCount: ComputedRef<number> = computed(() => {
  let total: number = 0;
  if (result.value) {
    total = Object.values(result.value).reduce((acc, curr) => acc + curr, 0);
  }
  return total;
});
async function loadData() {
  setFilter(Filters.SECTORS, selectedSectorsComputed.value);
  await fetchData();
}

watch(
  () => selectedSectorsComputed.value,
  async () => await loadData(),
);

onMounted(loadData);
</script>

<template>
  <dashboard-card
    :title="t('dashboard.certificates.title')"
    :is-loading="isLoading"
    :error="error"
    body-class="h-full flex flex-col"
    container-class="h-full"
    loader-container-class="mt-14 mb-14"
  >
    <h6 v-if="totalCount === 0" class="text-gray-700">
      {{ t("dashboard.certificates.noData") }}
    </h6>
    <div
      v-else
      class="grid grid-cols-[24px_max-content_1fr] auto-rows-fr font-medium items-center pb-2 h-full"
    >
      <template v-for="item in certificateData" :key="item.key">
        <dtwin-certificate-status-icon :icon="item.icon" />
        <span
          class="text-ellipsis overflow-hidden whitespace-nowrap ml-4 min-w-0"
          >{{ item.label }}</span
        >
        <div
          :style="{
            color: item.color,
          }"
          class="border-l border-gray-300 ml-1 pl-3 w-full flex items-center h-4"
        >
          <div
            class="h-4"
            :style="{
              width: `${item.percentage}%`,
              background: item.color,
            }"
          ></div>
          <span class="ml-2">({{ item.count }})</span>
        </div>
      </template>
    </div>
  </dashboard-card>
</template>
